import { DatePipe, CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { LOAD_SIZE_FTL, LOAD_SIZE_LTL, STATUS_ASSIGNED, STATUS_CONFIRMATION_PENDING, STATUS_DELIVERED } from '../shared/constants';
import { SHIPMENTS_HISTORY, SHIPMENTS_ROUTE, SHIPMENTS_ROUTE_MY_SHIPMENTS } from '../shared/routes';
import { ShipmentsActionSheetComponent } from '../shipments-action-sheet/shipments-action-sheet.component';


const TYPE_PICKUP = 'pickUp';
const TYPE_DROP = 'dropOff';
const MM_DD_YYYY = 'MM/dd/yyyy';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/dd/yyyy'
  },
  display: {
    dateInput: 'MM/dd/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy'
  }
};

@Component({
  selector: 'app-filter-shipments',
  templateUrl: './filter-shipments.component.html',
  styleUrls: ['./filter-shipments.component.scss'],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    DatePipe
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
    MatButtonModule
  ]
})
export class FilterShipmentsComponent implements OnInit {
  filterForm: UntypedFormGroup;
  loadSizes: string[] = [LOAD_SIZE_FTL, LOAD_SIZE_LTL];
  statusList: string[] = [STATUS_CONFIRMATION_PENDING, STATUS_ASSIGNED];
  historyStatusList: string[] = [STATUS_DELIVERED]
  countryMap = { Canada: 'CA', USA: 'US' };

  @ViewChild('pickUpText', { static: false }) pickUpText: any;
  @ViewChild('dropText', { static: false }) dropText: any;
  @ViewChild('fromDatePicker', { static: false }) fromDatePicker: any;
  @ViewChild('toDatePicker', { static: false }) toDatePicker: any;
  pageType: string = '';
  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly bottomSheet: MatBottomSheet,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.filterForm = this.createForm();
    let filters: any = window.history.state?.filter || {};
    if (filters) {
      // Convert string dates back to Date objects for proper display
      if (filters.fromDate && typeof filters.fromDate === 'string') {
        filters.fromDate = new Date(filters.fromDate);
      }
      if (filters.toDate && typeof filters.toDate === 'string') {
        filters.toDate = new Date(filters.toDate);
      }
      this.filterForm.patchValue({
        ...filters
      });
    }
    this.statusList = this.statusList.map(el => this.convertToTitleCase(el))
    this.historyStatusList = this.historyStatusList.map(el => this.convertToTitleCase(el))
    this.pageType = window.history.state?.pageType
  }

  ngAfterViewInit(): void {
    this.getPlaceAutocomplete(this.pickUpText, TYPE_PICKUP);
    this.getPlaceAutocomplete(this.dropText, TYPE_DROP);
  }

  private convertToTitleCase(str: string): string {
    return str.replace(/\w\S*/g, t => t.charAt(0).toUpperCase() + t.substr(1));
  }

  private createForm(): UntypedFormGroup {
    const form = this.fb.group({
      fromDate: [''],
      toDate: [''],
      pickUp: [''],
      pickUpCity: [''],
      pickUpState: [''],
      dropOff: [''],
      dropOffCity: [''],
      dropOffState: [''],
      loadSize: [''],
      status: [''],
    })
    return form;
  }

  get fromDate() {
    return this.filterForm.get('fromDate');
  }
  get toDate() {
    return this.filterForm.get('toDate');
  }

  get pickUp() {
    return this.filterForm.get('pickUp');
  }

  get dropOff() {
    return this.filterForm.get('dropOff');
  }

  get loadSize() {
    return this.filterForm.get('loadSize');
  }

  get status() {
    return this.filterForm.get('status');
  }



  onNavigateBack(): void {
    this.navigateToShipmentsList();
  }

  onOpenLoadSizeActionSheet(): void {
    this.openActionSheet("Load Size", this.loadSizes, this.loadSize.value, 'loadSize');
  }
  onOpenStatusActionSheet(): void {
    if (this.pageType === 'history') {
      this.openActionSheet("Status", this.historyStatusList, this.status.value, 'status');
    } else {
      this.openActionSheet("Status", this.statusList, this.status.value, 'status');
    }
  }

  openActionSheet(title: string, options: string[], selectedValue: string, field: string): void {
    this.bottomSheet.open(ShipmentsActionSheetComponent, {
      data: {
        type: 'filter',
        title: title,
        options: options,
        selectedValue: selectedValue,
        onConfirm: (val: string) => {
          this.filterForm.patchValue({
            [field]: val
          })
        }
      }
    });
  }

  onApplyFilters() {
    this.navigateToShipmentsList();
  }

  onClearFilters() {
    this.filterForm.reset();
  }

  navigateToShipmentsList() {
    // Format dates for navigation/API
    const formValue = { ...this.filterForm.value };
    if (formValue.fromDate instanceof Date) {
      formValue.fromDate = this.datePipe.transform(formValue.fromDate, MM_DD_YYYY);
    }
    if (formValue.toDate instanceof Date) {
      formValue.toDate = this.datePipe.transform(formValue.toDate, MM_DD_YYYY);
    }
    
    if (this.pageType === 'history') {
      this.router.navigate([`/${SHIPMENTS_HISTORY}`], { state: { filter: formValue } });
    } else {
      this.router.navigate([`/${SHIPMENTS_ROUTE}/${SHIPMENTS_ROUTE_MY_SHIPMENTS}`], { state: { filter: formValue } });
    }
  }

  formatAddressAndAlign(formattedAddess: string, formControlName: string, type: string): void {
    const address = formattedAddess?.split(',');
    const countrySuffix = this.countryMap[address?.[2]?.trim()];
    const addressTxt = `${address?.[0]},${address?.[1]?.trim().split(" ")[0]}, ${countrySuffix}`;
    if (type === TYPE_PICKUP) {
      this.filterForm.patchValue({
        [formControlName]: addressTxt,
        pickUpCity: address?.[0],
        pickUpState: address?.[1]?.trim().split(" ")[0]
      });
    } else {
      this.filterForm.patchValue({
        [formControlName]: addressTxt,
        dropOffCity: address?.[0],
        dropOffState: address?.[1]?.trim().split(" ")[0]
      });
    }
    this.changeDetectorRef.detectChanges();
  }

  private getPlaceAutocomplete(addressText: any, type: string): void {
    if (!addressText?.nativeElement) {
      return;
    }

    const input = addressText.nativeElement;

    // Clean up existing autocomplete instance if it exists
    if (input.autocompleteInstance) {
      google.maps.event.clearInstanceListeners(input);
      input.autocompleteInstance = null;
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(addressText.nativeElement, {
        types: ['(cities)'],
        componentRestrictions: {
          country: [this.countryMap.Canada, this.countryMap.USA],
        },
        fields: ['formatted_address', 'address_components', 'geometry']
      });

      // Set fields explicitly to reduce payload and improve mobile performance
      autocomplete.setFields(['formatted_address', 'address_components', 'geometry']);

      google.maps.event.addListener(autocomplete, 'place_changed', () => {
        const place = autocomplete.getPlace();

        // Check if place has required data
        if (place && place.formatted_address) {
          try {
            const formattedAddress = place.formatted_address;
            this.formatAddressAndAlign(formattedAddress, addressText.nativeElement.name, type);
          } catch (error) {
            console.error('Error processing place selection:', error);
          }
        }
      });

      // Mobile-specific: Ensure autocomplete works well on mobile
      // Note: Removed focus event trigger to prevent Google Maps API errors

      // Store autocomplete instance in the element for later removal
      input.autocompleteInstance = autocomplete;
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }

    return addressText;
  }

  onDateChangeEvent(event: MatDatepickerInputEvent<Date>, field: string) {
    if (event.value) {
      // Store the Date object directly in the form control for proper display
      this.filterForm.patchValue({
        [field]: event.value
      });
      
      // Programmatically close the datepicker
      if (field === 'fromDate' && this.fromDatePicker) {
        this.fromDatePicker.close();
      } else if (field === 'toDate' && this.toDatePicker) {
        this.toDatePicker.close();
      }
    }
  }

  onAddressChange(event: MatDatepickerInputEvent<Date>, field: string) {
    if (!event.value && field == TYPE_PICKUP) {
      this.filterForm.patchValue({
        pickUp: '',
        pickUpCity: '',
        pickUpState: ''
      });
    } else if (!event.value && field == TYPE_DROP) {
      this.filterForm.patchValue({
        dropOff: '',
        dropOffCity: '',
        dropOffState: ''
      });
    }
  }

}
